/**
 * LOGGING SYSTEM: Comprehensive logging for data isolation debugging
 * Tracks all data access, vestiging switches, and data visibility
 */

const LogLevels = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR'
};

class DataIsolationLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500; // Keep last 500 logs
    // Default: show INFO+, store logs only when explicitly enabled
    this.enableConsole = false; // Disabled to reduce console noise
    this.enableStorage = false;
    this.levelThreshold = LogLevels.INFO; // DEBUG messages will be ignored by default
  }
  log(level, source, message, data = null) {
    // Level filtering
    const order = { DEBUG: 10, INFO: 20, WARN: 30, ERROR: 40 };
    if (order[level] < order[this.levelThreshold]) return;

    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, level, source, message, data };

    // Compact structured console output
    if (this.enableConsole) {
      const out = { t: timestamp, l: level, s: source, m: message };
      if (data) out.d = data;
      // Use console methods matching level for better filtering in DevTools
      if (level === LogLevels.ERROR) console.error('[DI]', out);
      else if (level === LogLevels.WARN) console.warn('[DI]', out);
      else if (level === LogLevels.DEBUG) console.debug('[DI]', out);
      else console.info('[DI]', out);
    }

    // Store in memory (for export) if enabled
    if (this.enableStorage) {
      this.logs.push(logEntry);
      if (this.logs.length > this.maxLogs) this.logs.shift();

      try {
        sessionStorage.setItem('dataIsolationLogs', JSON.stringify(this.logs));
      } catch {
        // ignore storage errors
      }
    }
  }

  getStyle(level) {
    const styles = {
      DEBUG: 'color: #888; font-weight: normal;',
      INFO: 'color: var(--info); font-weight: bold;',
      WARN: 'color: var(--warning); font-weight: bold;',
      ERROR: 'color: var(--error); font-weight: bold;'
    };
    return styles[level] || 'color: #666;';
  }

  // Public methods for logging
  debug(source, message, data) {
    this.log(LogLevels.DEBUG, source, message, data);
  }

  info(source, message, data) {
    this.log(LogLevels.INFO, source, message, data);
  }

  warn(source, message, data) {
    this.log(LogLevels.WARN, source, message, data);
  }

  error(source, message, data) {
    this.log(LogLevels.ERROR, source, message, data);
  }

  // Data isolation specific logging
  logUserContext(userId, isSuperAdmin, userBranchId, userEmail) {
    this.info('USER_CONTEXT', '👤 User Context Initialized', {
      userId,
      isSuperAdmin,
      userBranchId,
      userEmail
    });
  }

  logVestigingSwitch(oldVestiging, newVestiging, userId) {
    this.info('VESTIGING_SWITCH', `🏢 Vestiging switched: ${oldVestiging} → ${newVestiging}`, {
      userId,
      oldVestiging,
      newVestiging,
      timestamp: new Date()
    });
  }

  logDataQuery(vestigingId, collectionName, count, isSuperAdmin) {
    const prefix = isSuperAdmin ? '⭐' : '👤';
    this.info('DATA_QUERY', `${prefix} Loaded ${count} items from ${collectionName}`, {
      vestigingId,
      collectionName,
      count,
      isSuperAdmin
    });
  }

  logDataIsolation(vestigingId, userBranchId, allowed) {
    const icon = allowed ? '✅' : '❌';
    this.info('DATA_ISOLATION', `${icon} Access check: ${vestigingId} vs ${userBranchId}`, {
      vestigingId,
      userBranchId,
      allowed
    });
  }

  logSuperAdminAccess(action, vestigingCount) {
    this.warn('SUPER_ADMIN', `⭐ Super Admin accessing ${action} across ${vestigingCount} vestigingen`, {
      action,
      vestigingCount
    });
  }

  logBranchIdValidation(branchId, isValid, reason) {
    const icon = isValid ? '✅' : '❌';
    this.info('BRANCHID_VALIDATION', `${icon} BranchId validation: ${branchId}`, {
      branchId,
      isValid,
      reason
    });
  }

  logCollectionPath(path, count) {
    this.debug('COLLECTION_PATH', `📂 Collection path: ${path}`, { itemCount: count });
  }

  // Export logs for debugging
  exportLogs() {
    return {
      timestamp: new Date().toISOString(),
      totalLogs: this.logs.length,
      logs: this.logs,
      summaryByLevel: this.getSummary()
    };
  }

  getSummary() {
    const summary = {
      DEBUG: 0,
      INFO: 0,
      WARN: 0,
      ERROR: 0
    };

    this.logs.forEach(log => {
      summary[log.level]++;
    });

    return summary;
  }

  // Clear logs
  clear() {
    this.logs = [];
    if (this.enableStorage) {
      sessionStorage.removeItem('dataIsolationLogs');
    }
  }

  // Print summary to console
  printSummary() {
    const summary = this.getSummary();
    console.table(summary);
    console.log(`Total logs: ${this.logs.length}`);
  }
}

// Create singleton instance
export const logger = new DataIsolationLogger();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.dataIsolationLogger = logger;
}

/**
 * Helper function to check data isolation
 */
export function checkDataIsolation(userBranchId, isSuperAdmin, targetVestiging) {
  if (isSuperAdmin) {
    logger.logSuperAdminAccess('All Vestigingen', 1);
    return true;
  }

  const allowed = userBranchId === targetVestiging;
  logger.logDataIsolation(targetVestiging, userBranchId, allowed);

  if (!allowed) {
    logger.error('DATA_ISOLATION', `🚫 BLOCKED: User ${userBranchId} cannot access ${targetVestiging}`, {
      userBranchId,
      targetVestiging,
      isSuperAdmin
    });
  }

  return allowed;
}
